import { CreateLogger } from "@swivl-mono/helpers";
import {
  ActionTaken,
  ActionTakenPayload,
  ActionTakenType,
  AuthPayloadInterface,
  From,
  LanguageChangePayload,
  Log,
  MessageAuth,
  MessageInterface,
  MessageType,
  OperatorVisibleCommandTypes,
  TypingPayloadActionType,
  UserShouldMergePayload,
  VarKey,
  createActionTaken,
  createActionTakenMessage,
  createAuthMessage,
  createCreateUserAndStartConversationMessage,
  createStartConversationMessage,
  createTextMessage,
  createTypingMessage
} from "@swivl-mono/types";

import { Config } from "../Config/Config";
import { SwivlChat } from "../SwivlChat";
import { GetTime } from "../helpers/GetTime";
import { getBrowserPreloadLanguage, getPreloadLanguage } from "../i18n/strings";

const log = CreateLogger(Log.CHAT, "MessageHandler");

export interface MessageHandlerInterface {
  handleMessage(message: MessageInterface): void;
  sendTextMessage(text: string): void;
  actionTaken(messageId: string, actionTaken: ActionTaken): void;
  loadPastMessages(olderThanMessage?: MessageInterface): Promise<Array<MessageInterface>>;
  getNewLocations(messageId: number, messageUUID: string, zipCode: string, searchRadiusInMeters: number);
}

export class MessageHandler implements MessageHandlerInterface {
  delegate: SwivlChat;
  messageIdsSent: string[];

  hasSoundLoaded = false;
  dingSound?: HTMLAudioElement;

  constructor(delegate: SwivlChat) {
    this.delegate = delegate;
    this.messageIdsSent = [];
  }

  handleMessage(message: MessageInterface): void {
    if (message?.actionTaken && Object.values(message.actionTaken).length === 0) {
      delete message.actionTaken;
    }
    log("this.delegate.props.channel !== message.channel :", this.delegate?.props?.channel, "!==", message.channel);

    if (this.delegate.props.channel && this.delegate.props.channel !== message.channel) {
      console.warn("Message channel does not match the current channel", message, message.channel, this.delegate.props.channel);
      return;
    }

    if (message.type === MessageType.UserShouldMerge) {
      this.handleUserShouldMerge(message);
      return;
    }
    if (message.type === MessageType.Auth) {
      this.handleAuthMessage(message);
      return;
    }
    if (message.type === MessageType.LanguageChange) {
      const payload = message.payload as LanguageChangePayload;
      if (payload?.initial && getPreloadLanguage()) {
        return;
      }
      this.delegate.setLanguage(payload?.language);
      return;
    }
    if (message.type === MessageType.Text) {
      this.playSoundIfNeededForMessage(message);
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.ProfileCollect) {
      this.handleGenericMessage(message);
      return;
    }

    if (message.type === MessageType.Typing) {
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.Buttons) {
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.Carousel) {
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.LocationPicker) {
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.SpacePicker) {
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.TakeoverEvent) {
      this.loadSoundIfNeeded(false);
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.MultiSelect) {
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.Media) {
      this.handleGenericMessage(message);
      return;
    }
    if (message.type === MessageType.Command) {
      const commandType = (message.payload as any)?.type;
      if (commandType && OperatorVisibleCommandTypes.includes(commandType)) {
        message.isSystemEvent = true;
      }
      this.handleGenericMessage(message);
      this.delegate.commandHandler.handleCommandMessage(message);
      return;
    }

    if (message.type === MessageType.StartConversation || message.type === MessageType.ActionTaken) {
      message.isSystemEvent = true;
      if (message.type === MessageType.ActionTaken) {
        const payload = message.payload as ActionTakenPayload | undefined;
        if (payload?.messageId && payload?.actionTaken) {
          this.delegate.list?.addActionTakenToItemInList(payload.messageId, payload.actionTaken);
        }
      }
      this.handleGenericMessage(message);
      return;
    }

    console.warn("Unhandled Message Type", message);
  }

  handleAuthMessage(message: MessageInterface): void {
    let payload: AuthPayloadInterface = message.payload as AuthPayloadInterface;
    if (payload) {
      if (payload.from === From.AGENT) {
        return;
      }
      if (this.delegate.state.auth?.from === From.AGENT) {
        return;
      }
      this.delegate.updateAuth(message.payload as AuthPayloadInterface);
    } else {
      console.warn("Invalid Auth Message", message);
    }
  }
  handleUserShouldMerge(message: MessageInterface): void {
    console.log("handleUserShouldMerge :", message);
    const payload: UserShouldMergePayload = message.payload as UserShouldMergePayload;
    this.delegate.updateAuth({ ...this.delegate.state.auth, userId: payload.mergeToAuth.userId, token: payload.mergeToAuth.token } as any);
    // this.delegate.socketHandler.disconnect();
    // this.delegate.socketHandler.connect();
    setTimeout(() => {
      this.delegate.list?.loadPastMessages();
    }, 2000);
  }
  handleGenericMessage(message: MessageInterface): void {
    if (
      this.delegate.state.auth &&
      message.from &&
      // !this.messageIdsSent.includes(message.id) &&
      this.delegate.list
    ) {
      this.delegate.list?.addMessagesToList([message]);
    } else {
    }
  }

  /**
   * This method sends the message to the socket handler as well as the message list.
   * @param  {[type]} message [description]
   * @return {[type]}         [description]
   */
  private sendMessage(message: MessageInterface) {
    this.messageIdsSent.push(message.uuid);
    this.delegate.socketHandler.sendMessage(message);
    if (this.delegate.list) {
      this.delegate.list?.addMessagesToList([message]);
    }
  }

  sendTypingFrame(isTyping: boolean) {
    if (!this.delegate.state.auth) {
      console.warn("no auth");
      return;
    }
    const type = isTyping ? TypingPayloadActionType.Started : TypingPayloadActionType.Ended;
    const message = createTypingMessage(this.delegate.state.auth.from, this.delegate.state.auth!.orgId, this.delegate.state.auth!.userId, type);
    this.sendMessage(message);
  }

  sendAuthMessage() {
    if (this.delegate.state.auth && this.delegate.state.auth.orgId && this.delegate.state.auth.userId && this.delegate.state.auth.token) {
      const message = createAuthMessage(
        this.delegate.state.auth.from,
        this.delegate.state.auth.orgId,
        this.delegate.state.auth.userId,
        this.delegate.state.auth.token
      );

      this.sendMessage(message);
    } else {
      console.warn("No auth", this.delegate.state.auth);
    }
  }

  sendCreateUserAndStartConversation() {
    let startOnQuest = Config && Config.startOnQuest ? Config.startOnQuest : null;
    if (this.delegate.props.startOnQuestId) {
      startOnQuest = this.delegate.props.startOnQuestId;
    }

    let preloadVariables = this.delegate.props.preloadVariables || {};
    if (Config.preloadVariables) {
      preloadVariables = { ...Config.preloadVariables, ...preloadVariables };
    }
    if (this.delegate.props.isMobile) {
      preloadVariables = { ...preloadVariables, [VarKey.UserIsMobile]: "1" };
    }
    if (!preloadVariables[VarKey.UserLanguage]) {
      const browserLanguage = getBrowserPreloadLanguage();
      if (browserLanguage) {
        preloadVariables = { ...preloadVariables, [VarKey.UserLanguage]: browserLanguage };
      }
    }

    const message = createCreateUserAndStartConversationMessage(From.USER, Config.orgId, startOnQuest as any, preloadVariables);

    this.sendMessage(message);
  }

  sendStartConversation() {
    let startOnQuest = Config && Config.startOnQuest ? Config.startOnQuest : null;
    if (this.delegate.props.startOnQuestId) {
      startOnQuest = this.delegate.props.startOnQuestId;
    }
    let preloadVariables = this.delegate.props.preloadVariables;
    if (Config.preloadVariables) {
      preloadVariables = { ...Config.preloadVariables, ...preloadVariables };
    }
    if (this.delegate.props.isMobile) {
      preloadVariables = { ...preloadVariables, [VarKey.UserIsMobile]: "1" };
    }
    if (!preloadVariables?.[VarKey.UserLanguage]) {
      const browserLanguage = getBrowserPreloadLanguage();
      if (browserLanguage) {
        preloadVariables = { ...preloadVariables, [VarKey.UserLanguage]: browserLanguage };
      }
    }
    if (!this.delegate.state.auth) {
      console.warn("No auth");
      return;
    }
    let message = createStartConversationMessage(
      this.delegate.state.auth.from,
      this.delegate.state.auth.orgId,
      this.delegate.state.auth.userId,
      startOnQuest as any,
      preloadVariables,
      this.delegate.isNewUser
    );
    console.log("sendStartConversation :", message, this.delegate, this.delegate.isNewUser);

    this.sendMessage(message);
  }
  sendTextMessage(text: string) {
    this.delegate.hideModal();
    if (!this.delegate.state.auth) {
      console.warn("No auth");
      return;
    }
    console.log("TESTINGSEND sendTextMessage :", text, this.delegate.state.auth);
    this.sendMessage(createTextMessage(this.delegate.state.auth.from, this.delegate.state.auth.orgId, this.delegate.state.auth.userId, text, false));
  }
  actionTaken(messageId: string, actionTaken: ActionTaken, hideFromList?: boolean) {
    if (this.delegate.props.transcriptOnly) {
      return;
    }

    if (!hideFromList) {
      this.delegate.list?.addActionTakenToItemInList(messageId, actionTaken);
    }
    if (!this.delegate.state.auth) {
      console.warn("No auth");
      return;
    }
    const message = createActionTakenMessage(
      this.delegate.state.auth.from,
      this.delegate.state.auth.orgId,
      this.delegate.state.auth.userId,
      messageId,
      actionTaken
    );

    this.delegate.socketHandler.sendMessage(message);
  }

  loadTranscript(transcriptDate: Date): Promise<Array<MessageInterface>> {
    const auth: any = this.delegate.state.auth;
    if (!auth) {
      console.warn("No user");
      return Promise.reject("No user");
    }
    // if (olderThanMessage && (olderThanMessage.created || olderThanMessage.received)) {
    //   if (olderThanMessage.received) {
    //     olderThan = `&olderThan=${olderThanMessage.received}`
    //   } else {
    //     olderThan = `&olderThan=${olderThanMessage.created}`
    //   }
    // }
    if (!transcriptDate) {
      transcriptDate = new Date();
    }
    log("transcriptDate :", typeof transcriptDate, transcriptDate);
    if (typeof transcriptDate === "string") {
      transcriptDate = new Date(transcriptDate);
    }
    let query = `?olderThan=${transcriptDate.toISOString()}`;
    if (this.delegate.props.channel) {
      query += `&channel=${this.delegate.props.channel}`;
    }
    if (this.delegate.props.showSystemEvents) {
      query += `&includeSystemEvents=true`;
    }
    const server = this.delegate.props.server.includes("http") ? this.delegate.props.server : `https://${this.delegate.props.server}`;

    const URL = `${server}/api/messages/org/${auth.orgId}/user/${auth.userId}/token/${auth.token}/limit/20${query}`;

    return fetch(URL).then((data) => {
      return data.json();
    });
  }

  loadPastMessages(olderThanMessage?: MessageInterface): Promise<Array<MessageInterface>> {
    const auth: any = this.delegate.state.auth;
    if (!auth) {
      console.warn("No user");
      return Promise.reject();
    }
    let query = "?";
    if (olderThanMessage && olderThanMessage.createdAt) {
      query += `olderThan=${olderThanMessage.createdAt}`;
    }
    if (this.delegate.props.channel) {
      query += `&channel=${this.delegate.props.channel}`;
    }
    if (this.delegate.props.showSystemEvents) {
      query += `&includeSystemEvents=true`;
    }
    const server = this.delegate.props.server.includes("http") ? this.delegate.props.server : `https://${this.delegate.props.server}`;
    const URL = `${server}/api/messages/org/${auth.orgId}/user/${auth.userId}/token/${auth.token}/limit/20${query}`;

    return fetch(URL).then((data) => {
      return data.json();
    });
  }

  getNewLocations(messageId: number, messageUUID: string, zipCode: string, searchRadiusInMeters: number) {
    // const auth:any  = this.delegate.state.auth;

    // const URL = `${this.delegate.props.server}/lookup-table/storefinder/update-message?botUserId=${auth.botUserId}&token=${auth.token}&zip=${zipCode}&messageId=${messageId}&searchRadiusInMeters=${searchRadiusInMeters}`;
    const actionTaken = createActionTaken(ActionTakenType.Other, "postalCode", {
      messageId: messageId,
      messageUUID,
      postalCode: zipCode,
      searchRadiusInMeters: searchRadiusInMeters
    });

    this.delegate.messageHandler.actionTaken(messageUUID, actionTaken, true);
    let promise = new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(true);
      }, 1500);
    });
    return promise;
    //   return fetch(URL).then(data=>{
    //     if (data.status > 250) {
    //       return data.json().then(r => { console.warn("Error fetching locations",r); Promise.reject(r) })
    //     }
    //     return data.json().then(r =>{

    //       this.delegate.list?.updateMessage(messageId, r)
    //   })
    // });
  }

  loadSoundIfNeeded(shouldPlayOnLoad: boolean) {
    if (this.hasSoundLoaded) {
      return;
    }
    this.dingSound = new Audio("https://swivl-cdn.s3-us-west-1.amazonaws.com/message_alert.m4a");
    this.dingSound.volume = 0.5;
    this.dingSound.oncanplaythrough = (event) => {
      this.hasSoundLoaded = true;
      if (shouldPlayOnLoad) {
        this.dingSound?.play();
      }
    };
  }
  playDing() {
    if (this.hasSoundLoaded) {
      this.dingSound?.play();
    } else {
      this.loadSoundIfNeeded(true);
    }
  }
  playSoundIfNeededForMessage(message: MessageInterface) {
    if ((!this.delegate.props.canTakeOver && message.from === From.AGENT) || (this.delegate.props.canTakeOver && message.from === From.USER)) {
      this.playDing();
    }
  }
}
